import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ActionListsApiService } from '../action-lists.api.service';
import { ActionListAccountGroup, ActionListAccountsResponse } from '../action-lists.api.service.response';

export interface ActionListAccount {
  accountGroupId: string;
  name: string;
  fullAddress: string;
  modified: string;
}

@Injectable({ providedIn: 'root' })
export class ActionListsAccountsStoreService {
  private accounts$$: BehaviorSubject<ActionListAccount[]> = new BehaviorSubject([]);
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private hasMore$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private pageNumber$$: BehaviorSubject<number> = new BehaviorSubject(0);
  private actionListId$$: Subject<string> = new Subject();
  private totalAccounts$$: Subject<number> = new Subject();

  public loading$: Observable<boolean> = this.loading$$.asObservable();
  public hasMore$: Observable<boolean> = this.hasMore$$.asObservable();
  public accounts$: Observable<ActionListAccount[]> = this.accounts$$.asObservable();
  public totalAccounts$: Observable<number> = this.totalAccounts$$.asObservable();

  constructor(private actionListsApiService: ActionListsApiService) {
    this.init();
  }

  loadAccounts(actionListId: string): void {
    this.accounts$$.next([]);
    this.pageNumber$$.next(0);
    this.actionListId$$.next(actionListId);
  }

  loadMore(): void {
    if (this.hasMore$$.getValue()) {
      const currentPage: number = this.pageNumber$$.getValue();
      this.pageNumber$$.next(currentPage + 1);
    }
  }

  private init(): void {
    combineLatest([this.actionListId$$, this.pageNumber$$])
      .pipe(
        switchMap(([actionListId, pageNumber]) => {
          this.loading$$.next(true);
          return this.actionListsApiService.getActionListAccounts(actionListId, pageNumber);
        }),
      )
      .subscribe(
        (response: ActionListAccountsResponse) => {
          this.handleResponse(response);
          this.loading$$.next(false);
        },
        () => {
          this.loading$$.next(false);
        },
      );
  }

  private handleResponse(response: ActionListAccountsResponse): void {
    this.hasMore$$.next(Boolean(response.page));
    this.totalAccounts$$.next(response.total_accounts);
    let accounts: ActionListAccount[] = [];

    if (this.pageNumber$$.getValue() > 0) {
      accounts = this.accounts$$.getValue();
    }
    accounts = accounts.concat(
      response.accounts.map((account) => ({
        accountGroupId: account.accountGroupId,
        name: account.companyName,
        fullAddress: this.fullAddress(account),
        modified: account.updatedDateTime,
      })),
    );
    this.accounts$$.next(accounts);
  }

  private fullAddress(account: ActionListAccountGroup): string {
    const parts = [account.address, account.city, account.state, account.zip].filter((part) => !!part);
    return parts.join(', ');
  }

  public removeAccount(accountGroupId: string, actionListId: string): void {
    this.actionListsApiService
      .removeAccount(accountGroupId, actionListId)
      .pipe(take(1))
      .subscribe(() =>
        this.accounts$$.next(this.accounts$$.getValue().filter((ag) => ag.accountGroupId !== accountGroupId)),
      );
  }
}
